/**
 * Pure row-axis logic for the Plan Entry grid, extracted so it can be unit-tested without rendering
 * the MUI DataGrid (whose CSS import vitest can't transform). The grid component computes the row
 * axes + read-back maps from hooks and delegates the data transformation to these functions.
 *
 * A PlanView can carry several row dimensions (ordered by SortOrder); the grid renders the OUTER ones
 * as nested grouping levels and the INNERMOST as the leaf data rows — i.e. the cartesian product of
 * every row dimension's value-set, grouped. At most one axis may be entity-backed hierarchical
 * (HIERARCHY_CHILDREN), because the cell-save engine carries a single ProductGroupId.
 */

/** One value of a row dimension: a free DimensionValue, a hierarchy node, or a flat entity record. */
export interface RowItem {
  key: string;          // stable row key (the value id, or the entity id)
  label: string;
  rowValueId?: string;  // set for free-dimension values
  productGroupId?: string; // set for HIERARCHY_CHILDREN (product-group) nodes
  customerId?: string;  // set for SALES_CUSTOMER (flat entity-backed) rows
  tradeTypeId?: string; // set for CORE_TRADE_TYPE (flat entity-backed) rows
  supplierId?: string;  // set for PROCUREMENT_SUPPLIER (flat entity-backed) rows
  accountId?: string;   // set for ACCOUNT-axis rows (a GL account — the row key IS the account)
  code?: string;        // entity code — maps a saved entity-backed value back to this row
  // Server-resolved EFFECTIVE unit of a product-group node (nearest ancestor wins — internal-
  // pricelist spec §12). Set only on group items whose group resolves a unit; drives the
  // row-label suffix and the per-unit totals bucketing.
  unitOfMeasureId?: string;
  unitCode?: string;    // the unit's display code, e.g. "ha" — the "(ha)" suffix text
}

/**
 * A row item's display label, suffixed with its effective unit when it has one —
 * "Markberedning (ha)" (internal-pricelist spec §12.3). Items without a unit (every non-group
 * axis, and groups with no resolvable unit) render exactly as before.
 */
export function rowItemLabel(item: RowItem): string {
  return item.unitCode ? `${item.label} (${item.unitCode})` : item.label;
}

/**
 * Sentinel axis id for the ACCOUNT row axis (rowSourceKind = ACCOUNT), which has no
 * DimensionDefinition. Several ACCOUNT fragments (multiplicity) union into one axis under this key,
 * exactly as same-dimension fragments do under their shared dimensionDefinitionId.
 */
export const ACCOUNT_AXIS_ID = '__ACCOUNT__';

/** The stored composable filter as a non-empty JSON string, or undefined when there is no usable filter. */
export function rowFilterJson(filterJson: unknown): string | undefined {
  if (filterJson == null) return undefined;
  const s = (typeof filterJson === 'string' ? filterJson : JSON.stringify(filterJson)).trim();
  return s && s !== 'null' ? s : undefined;
}

/**
 * Backings whose filtered row-values are resolved server-side (the owning module translates the filter
 * to SQL). Keep in sync with the backend dispatch in `RowValueResolver.ResolveAsync` — a backing must
 * appear in both to be filterable end-to-end.
 */
export const SERVER_RESOLVED_BACKINGS = new Set([
  'INVENTORY_PRODUCT_GROUP', 'SALES_CUSTOMER', 'CORE_TRADE_TYPE', 'PROCUREMENT_SUPPLIER',
]);

/**
 * Backings whose records form a tree, i.e. the only ones a HIERARCHY_CHILDREN row axis can enumerate
 * (its RootRef is a node of that tree). Every other backing is FLAT — its records are listed whole,
 * and it has no root to pick. Keep in sync with `RowValueResolver.ResolveAsync`, where
 * INVENTORY_PRODUCT_GROUP is the one branch that consumes `rootRef` at all; a flat backing ignores it.
 */
export const HIERARCHICAL_BACKINGS = new Set(['INVENTORY_PRODUCT_GROUP']);

/**
 * Whether this row axis can enumerate HIERARCHY_CHILDREN — an entity-backed dimension on a
 * hierarchical backing. A FREE dimension (values are DimensionValue rows) and a flat entity-backed one
 * (Customer, Trade Type, Supplier) carry no tree to root the axis in, so the enumeration is neither
 * offered in the authoring dialog nor honoured when a stale value lingers on a stored axis.
 */
export function supportsHierarchyChildren(
  backingEntity: string | undefined,
  valueSource: string | undefined,
): boolean {
  return valueSource === 'ENTITY_BACKED' && HIERARCHICAL_BACKINGS.has(backingEntity ?? '');
}

/**
 * Whether this row dimension's values are enumerated by the server rather than client-side. A stored
 * composable filter is what routes an axis there, and the same resolver answers for a FREE dimension
 * (its own DimensionValue rows, read in-module) as for the four entity backings (dispatched to the
 * owning module) — so a free dimension is server-resolved despite having no backing entity at all.
 * An axis with no usable filter stays client-side whatever its source.
 */
export function isServerResolved(
  filterJson: unknown,
  backingEntity: string | undefined,
  valueSource: string | undefined,
): boolean {
  return rowFilterJson(filterJson) !== undefined
    && (SERVER_RESOLVED_BACKINGS.has(backingEntity ?? '') || valueSource === 'FREE');
}

/**
 * The query/lookup key for an ACCOUNT row-axis fragment. Includes the AccountGroup subtree root
 * (accountGroupRootRef, for HIERARCHY_CHILDREN) AND the composable filter, so two account axes that
 * differ only by root — or only by filter — never share a resolve query, while identical fragments
 * dedupe. All account fragments still union under the ACCOUNT_AXIS_ID sentinel afterwards.
 */
export function accountAxisKey(dim: { accountGroupRootRef?: unknown; filterJson?: unknown }): string {
  return `${ACCOUNT_AXIS_ID}|${(dim.accountGroupRootRef as string) ?? ''}|${rowFilterJson(dim.filterJson) ?? ''}`;
}

/** One row dimension of the view with its enumerated value-set. */
export interface RowAxis {
  dimensionDefinitionId: string; // the dimension id, or ACCOUNT_AXIS_ID for an account axis
  name: string;
  isHierarchy: boolean;        // HIERARCHY_CHILDREN (product group) — the one-per-cell cardinality axis
  isAccount?: boolean;         // ACCOUNT axis — rows are GL accounts; saved cells map back via accountId
  items: RowItem[];
  // For an entity-backed axis (hierarchy or flat): map a saved cell's materialised DimensionValue id
  // to this axis's item key (valueId → valueCode → entity key). Absent for a free dim (key == value id)
  // and for an account axis (key == the saved cell's accountId directly).
  valueIdToKey?: (valueId: string) => string | undefined;
}

/** One row-dimension value of a saved cell, read back from the server. */
export interface SavedRowValue { dimensionDefinitionId: string; dimensionValueId: string }

/** A previously saved cell, read back from the server. */
export interface SavedCell {
  fiscalPeriodId: string;
  rowValues: SavedRowValue[];
  accountId?: string; // the line's GL account — places the cell on its ACCOUNT-axis row
  measureValues: Record<string, number>;
  mappingStatus?: string; // RESOLVED | PENDING — a PENDING cell flags the row's Internpris cell (spec §6.2)
  mappingReason?: string; // why the cell is pending (e.g. NO_INTERNAL_PRICE_MATCHED)
}

/** A built grid row: one cell of the cartesian product across the row axes. */
export interface EntryRow {
  id: string;            // ordered join of each axis item's key — unique per combination
  rowLabel: string;      // innermost axis label (the leaf row), unit-suffixed when the row has one
  dimensionValueIds: string[]; // the free-dimension values in this combination (sent on save)
  productGroupId?: string;     // the hierarchy (product-group) item, if any
  customerId?: string;         // the flat customer item, if any
  tradeTypeId?: string;        // the flat trade-type item, if any
  supplierId?: string;         // the flat supplier item, if any
  accountId?: string;          // the GL account item, if any (ACCOUNT row axis)
  // The row's effective unit (from its product-group item — spec §12) for the per-unit totals
  // bucketing. Display only; never part of the save payload (composeSaveCells ignores it).
  unitOfMeasureId?: string;
  unitCode?: string;
  // group label per OUTER axis keyed `g${index}`, plus numeric value per (period, measure) keyed
  // `${periodId}__${measureCode}`.
  [field: string]: unknown;
}

export interface PeriodLike { id: string; periodNumber?: number }
export interface MeasureLike { code: string; valueType?: string; kind?: string; formula?: string; storageBinding?: string; displayDecimals?: number | null }

/** Reserved pseudo-period prefix for the read-only Total column cells. Real period ids are GUIDs. */
export const TOTAL_FIELD_PREFIX = '__TOTAL__';

/**
 * Reserved field prefix for the single read-only Internpris column of an INTERNAL_REVENUE_LINE
 * measure (internal-pricelist spec §6.2): the rate is shown once per row, not per period, and is
 * server-sourced — the engine resolves it at save and reads it back from the linked revenue line.
 */
export const INTERNAL_PRICE_FIELD_PREFIX = '__IRL__';

/** Row field carrying the MappingReason when any of the row's saved cells is PENDING (spec §6.2). */
export const INTERNAL_PRICE_PENDING_FIELD = '__IRL_PENDING__';

/**
 * The measure bound to the linked internal-revenue line. Engine-resolved: excluded from client-side
 * DERIVED recompute and totals (both would blank a formula-less DERIVED measure on every edit), has
 * no per-period columns, and is never part of the save payload.
 */
export function isInternalRevenueMeasure(measure: MeasureLike): boolean {
  return measure.storageBinding === 'INTERNAL_REVENUE_LINE';
}

/**
 * OQ-1 advisory (internal-pricelist spec §1.2, settled by the domain owner 2026-07-11): the
 * internal price may equal or exceed, but never fall below, the purchase price entered on the row
 * — "kan vara högre men aldrig lägre". Non-blocking: the entry grid shows a warning; nothing
 * pends. Returns the internal price and the row's highest entered purchase price when the rule is
 * violated (strictly lower — equal is fine), else null. No warning while the row is PENDING (the
 * pending indicator already owns the cell) or when either side is absent.
 */
export function internalPriceBelowPurchasePrice(
  row: EntryRow,
  periods: PeriodLike[],
  measures: MeasureLike[],
): { internalPrice: number; purchasePrice: number } | null {
  const priceMeasure = measures.find(m => m.storageBinding === 'PLANLINE_UNITPRICE');
  if (!priceMeasure) return null;
  const internalPrice = rowInternalPrice(row, measures);
  if (internalPrice === null) return null;

  let purchasePrice: number | null = null;
  for (const p of periods) {
    const raw = row[`${p.id}__${priceMeasure.code}`];
    if (raw === undefined || raw === null || raw === '') continue;
    const value = Number(raw);
    if (Number.isNaN(value)) continue;
    if (purchasePrice === null || value > purchasePrice) purchasePrice = value;
  }
  if (purchasePrice === null || internalPrice >= purchasePrice) return null;
  return { internalPrice, purchasePrice };
}

/**
 * The row's resolved internal price (saved or previewed), or null when absent, non-numeric or the
 * row is PENDING — a pending row keeps its pending indicator; the OQ-1 advisory stays silent.
 */
export function rowInternalPrice(row: EntryRow, measures: MeasureLike[]): number | null {
  const irlMeasure = measures.find(isInternalRevenueMeasure);
  if (!irlMeasure) return null;
  if (row[INTERNAL_PRICE_PENDING_FIELD] !== undefined) return null;
  const raw = row[`${INTERNAL_PRICE_FIELD_PREFIX}${irlMeasure.code}`];
  const value = Number(raw);
  return raw === undefined || raw === null || raw === '' || Number.isNaN(value) ? null : value;
}

/** A measure is additive (its periods sum) when it is a QUANTITY or AMOUNT; rates/ratios are not. */
function isAdditiveMeasure(valueType: string | undefined): boolean {
  return valueType === 'QUANTITY' || valueType === 'AMOUNT';
}

/**
 * Decimal places for displaying a measure. A configured Measure.displayDecimals (0–4, set per
 * measure in the Plan View admin) wins and shows exactly that many decimals; otherwise the
 * valueType default applies: RATE / RATIO → 2 dp (prices, percentages); QUANTITY → 0 dp (whole
 * units); AMOUNT / unknown → 0–2 dp (kr with optional öre). Grouping is applied by the locale.
 */
export function measureFormatOptions(
  measure: Pick<MeasureLike, 'valueType' | 'displayDecimals'>,
): Intl.NumberFormatOptions {
  const { valueType, displayDecimals } = measure;
  if (typeof displayDecimals === 'number' && Number.isFinite(displayDecimals)) {
    const d = Math.min(4, Math.max(0, Math.trunc(displayDecimals)));
    return { minimumFractionDigits: d, maximumFractionDigits: d };
  }
  switch (valueType) {
    case 'RATE':
    case 'RATIO':
      return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    case 'QUANTITY':
      return { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    case 'AMOUNT':
    default:
      return { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  }
}

/** A row cell as a number, treating blank/undefined/NaN as 0. */
function cellNumber(row: EntryRow, periodId: string, code: string): number {
  const raw = row[`${periodId}__${code}`];
  if (raw === undefined || raw === null || raw === '') return 0;
  const value = Number(raw);
  return Number.isNaN(value) ? 0 : value;
}

/**
 * Kill binary floating-point noise in a sum of user-scale values (166.67 × 12 accumulates to
 * 2000.0000000000002 in doubles). The Total edit cell shows the raw number at full precision, so a
 * noisy sum leaks into the UI; month values themselves carry at most a few decimals, so rounding the
 * sum to 10 dp removes only the artifact, never real precision.
 */
export function cleanFloat(value: number): number {
  return Math.round(value * 1e10) / 1e10;
}

/**
 * The Total of a rate-like measure when there is NO volume to weight by (Σ Volym = 0).
 *
 * A rate is *broadcast* top-down, not spread: typing a price into the Total copies the SAME value
 * into every month (see `broadcastIntoRow`). So the row's month cells normally all carry that one
 * value — and that value simply IS the Total. Return it. Only when individual months were hand-edited
 * to differ is there no single broadcast value; then fall back to their plain mean so the Total still
 * reports the figures the row actually holds.
 *
 * It must NEVER go blank while a month carries a price. A blank Total is not merely misleading (the
 * price is plainly visible in the months next to it) — it is UNCLEARABLE: `applyRowEdit` only clears a
 * measure whose Total was non-blank BEFORE the edit, so backspacing a blank Total is a silent no-op.
 * That is what forced the "type a value, tab out, come back, backspace" dance, and it made clearing a
 * price impossible unless a volume happened to be present.
 */
function broadcastValueOfMonths(row: EntryRow, periods: PeriodLike[], code: string): number | '' {
  const values: number[] = [];
  for (const p of periods) {
    const raw = row[`${p.id}__${code}`];
    if (raw === undefined || raw === null || raw === '') continue;
    const value = Number(raw);
    if (!Number.isNaN(value)) values.push(value);
  }
  if (values.length === 0) return '';
  const [first] = values;
  if (values.every(v => v === first)) return first; // the broadcast value, verbatim
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
}

/**
 * Annual figure for a rate-like measure (e.g. Pris) = the volume-weighted average, i.e. the total of
 * the AMOUNT measure it drives ÷ the total of the QUANTITY it multiplies (Σ Värde / Σ Volym). A plain
 * mean of the monthly prices would ignore differing monthly volumes. The pairing is read from the
 * AMOUNT measure's product formula (e.g. "VOLUME * PRICE") — NOT from the measure's valueType, so a
 * price typed as RATIO in the view config still gets its weighted total. Rounded to 2 decimals (öre).
 *
 * With no volume to weight by (Σ Volym = 0), the weighted average is undefined but the row may still
 * hold prices — fall back to the broadcast value (see `broadcastValueOfMonths`). Returns '' only when
 * no AMOUNT measure pairs with the rate at all, or the row holds no value for it.
 */
function weightedRateTotal(
  rate: MeasureLike,
  measures: MeasureLike[],
  totalByCode: Record<string, number>,
  row: EntryRow,
  periods: PeriodLike[],
): number | '' {
  const operandsOf = (m: MeasureLike) =>
    (m.formula ?? '').includes('*') ? m.formula!.split('*').map(s => s.trim()) : [];
  const amount = measures.find(
    a => a.valueType === 'AMOUNT' && operandsOf(a).includes(rate.code),
  );
  if (!amount) return '';
  const qtyCode = operandsOf(amount).find(
    c => c !== rate.code && measures.find(mm => mm.code === c)?.valueType === 'QUANTITY',
  );
  if (qtyCode === undefined) return '';
  const denom = totalByCode[qtyCode];
  const numer = totalByCode[amount.code];
  if (typeof denom !== 'number' || denom === 0 || typeof numer !== 'number') {
    return broadcastValueOfMonths(row, periods, rate.code);
  }
  return Math.round((numer / denom) * 100) / 100;
}

/**
 * Fill the Total cells of a row in place. Additive measures (QUANTITY/AMOUNT) sum across `periods`;
 * any non-additive measure driving an AMOUNT product formula (Pris — whether typed RATE or RATIO in
 * the view config) gets the volume-weighted average (Σ Värde / Σ Volym, see `weightedRateTotal`);
 * non-additive measures without such a pairing get a blank total. Returns the same row. Pure — used
 * both on load and after each edit. The Total cells are display-only; never read by `composeSaveCells`.
 */
export function applyRowTotals(
  row: EntryRow,
  measures: MeasureLike[],
  periods: PeriodLike[],
): EntryRow {
  // Pass 1: additive measures sum across periods (needed before rate weighting).
  const totalByCode: Record<string, number> = {};
  for (const m of measures) {
    if (!isAdditiveMeasure(m.valueType)) continue;
    let sum = 0;
    for (const p of periods) sum += cellNumber(row, p.id, m.code);
    sum = cleanFloat(sum);
    totalByCode[m.code] = sum;
    row[`${TOTAL_FIELD_PREFIX}${m.code}`] = sum;
  }
  // Pass 2: non-additive measures — weighted average when an AMOUNT product formula pairs the
  // measure with a quantity (detection is formula-driven, so a price typed RATIO still totals),
  // blank otherwise (weightedRateTotal returns '' without a pairing). An INTERNAL_REVENUE_LINE
  // measure has its own server-sourced column and no Total cell — skipped entirely.
  for (const m of measures) {
    if (isAdditiveMeasure(m.valueType) || isInternalRevenueMeasure(m)) continue;
    row[`${TOTAL_FIELD_PREFIX}${m.code}`] = weightedRateTotal(m, measures, totalByCode, row, periods);
  }
  return row;
}

export interface SaveCell {
  fiscalPeriodId: string;
  dimensionValueIds: string[];
  productGroupId?: string;
  customerId?: string;
  tradeTypeId?: string;
  supplierId?: string;
  accountId?: string;
  measureValues: Record<string, number>;
  // Set when the user blanked a previously-entered cell: the row still carries its dimension
  // identity (so the server can find the natural key) but has no input values → delete the stored
  // line rather than upsert it. Absent for a normal value cell.
  clear?: boolean;
}

/** Stable key for one editable grid cell — a (row, period) intersection. Used for dirty tracking. */
export function cellKey(rowId: string, periodId: string): string {
  return `${rowId}::${periodId}`;
}

/** A cell value normalized for change detection: blank/undefined/null → '', else the Number. */
function normCell(raw: unknown): number | '' {
  if (raw === undefined || raw === null || raw === '') return '';
  const n = Number(raw);
  return Number.isNaN(n) ? '' : n;
}

/**
 * The period ids whose INPUT measure values differ between the committed `updated` row and the
 * `original` (pre-edit) row — i.e. the (row, period) cells this one edit actually dirtied. A single
 * cell edit changes exactly one period; a top-down spread/broadcast changes many at once. DERIVED
 * measures are ignored (they are recomputed, never sent). The grid unions these into the dirty set
 * so Save posts only the cells that changed since load.
 */
export function changedPeriodIds(
  updated: EntryRow,
  original: EntryRow,
  periods: PeriodLike[],
  inputMeasures: MeasureLike[],
): string[] {
  const changed: string[] = [];
  for (const p of periods) {
    for (const m of inputMeasures) {
      const field = `${p.id}__${m.code}`;
      if (normCell(updated[field]) !== normCell(original[field])) {
        changed.push(p.id);
        break;
      }
    }
  }
  return changed;
}

/**
 * Map a saved cell's per-axis values to a grid row id (the ordered combination of axis keys).
 * A dimension axis maps its saved value to its item key via `axis.valueIdToKey` (entity-backed) or
 * identity (free dim); an ACCOUNT axis places the cell by the line's `accountId` directly (the account
 * id IS the row key). Returns undefined if any axis value is missing/unmappable (a legacy/partial line).
 */
export function resolveSavedRowId(
  rowAxes: RowAxis[],
  cell: Pick<SavedCell, 'rowValues' | 'accountId'>,
): string | undefined {
  const byDef = new Map(cell.rowValues.map(rv => [rv.dimensionDefinitionId, rv.dimensionValueId]));
  const keys: string[] = [];
  for (const axis of rowAxes) {
    if (axis.isAccount) {
      if (!cell.accountId) return undefined;
      keys.push(cell.accountId);
      continue;
    }
    const valueId = byDef.get(axis.dimensionDefinitionId);
    if (!valueId) return undefined;
    const key = axis.valueIdToKey ? axis.valueIdToKey(valueId) : valueId;
    if (!key) return undefined;
    keys.push(key);
  }
  return keys.length > 0 ? keys.join('|') : undefined;
}

/**
 * Collapse row axes that share a `dimensionDefinitionId` into one logical axis (PlanView-RowAxis-Composition
 * Phase 1). The same dimension may be a row axis several times — each with its own root/source — and they are
 * the SAME axis, not a cross-product: their items are **unioned** (deduped by `key`, first-appearance order),
 * and their saved-value mappers are composed (try each in turn). Distinct dimensions are left as separate
 * axes, so the downstream cartesian product runs across distinct dimensions only.
 */
export function unionRowAxes(axes: RowAxis[]): RowAxis[] {
  const byDef = new Map<string, RowAxis>();
  const order: string[] = [];
  const mappers = new Map<string, Array<(valueId: string) => string | undefined>>();

  for (const axis of axes) {
    let merged = byDef.get(axis.dimensionDefinitionId);
    if (!merged) {
      merged = { dimensionDefinitionId: axis.dimensionDefinitionId, name: axis.name, isHierarchy: axis.isHierarchy, isAccount: axis.isAccount, items: [] };
      byDef.set(axis.dimensionDefinitionId, merged);
      mappers.set(axis.dimensionDefinitionId, []);
      order.push(axis.dimensionDefinitionId);
    }
    const seen = new Set(merged.items.map(i => i.key));
    for (const it of axis.items) if (!seen.has(it.key)) { merged.items.push(it); seen.add(it.key); }
    merged.isHierarchy = merged.isHierarchy || axis.isHierarchy;
    merged.isAccount = merged.isAccount || axis.isAccount;
    if (axis.valueIdToKey) mappers.get(axis.dimensionDefinitionId)!.push(axis.valueIdToKey);
  }

  for (const def of order) {
    const ms = mappers.get(def)!;
    if (ms.length > 0) {
      byDef.get(def)!.valueIdToKey = (valueId: string) => {
        for (const m of ms) { const k = m(valueId); if (k) return k; }
        return undefined;
      };
    }
  }

  return order.map(def => byDef.get(def)!);
}

/**
 * Cartesian product of every row axis's value-set → grid rows (outermost axis first), each pre-filled
 * from the saved cells. An empty axis (or no axes) yields no rows.
 */
export function buildEntryRows(
  rowAxes: RowAxis[],
  periods: PeriodLike[],
  measures: MeasureLike[],
  savedCells: SavedCell[] | undefined,
  resolveRowId: (cell: SavedCell) => string | undefined,
): EntryRow[] {
  if (rowAxes.length === 0 || rowAxes.some(a => a.items.length === 0)) return [];

  const saved = new Map<string, SavedCell>();
  for (const c of savedCells ?? []) {
    const rowId = resolveRowId(c);
    if (!rowId) continue;
    saved.set(`${rowId}|${c.fiscalPeriodId}`, c);
  }

  const internalRevenueMeasures = measures.filter(isInternalRevenueMeasure);

  let combos: RowItem[][] = [[]];
  for (const axis of rowAxes) {
    const next: RowItem[][] = [];
    for (const combo of combos) for (const item of axis.items) next.push([...combo, item]);
    combos = next;
  }

  const lastIdx = rowAxes.length - 1;
  const built: EntryRow[] = [];
  for (const combo of combos) {
    const id = combo.map(i => i.key).join('|'); // keys, never labels — ids survive the unit suffix
    const unitItem = combo.find(i => i.productGroupId);
    const row: EntryRow = {
      id,
      rowLabel: rowItemLabel(combo[lastIdx]),
      dimensionValueIds: combo.filter(i => i.rowValueId).map(i => i.rowValueId!),
      productGroupId: unitItem?.productGroupId,
      customerId: combo.find(i => i.customerId)?.customerId,
      tradeTypeId: combo.find(i => i.tradeTypeId)?.tradeTypeId,
      supplierId: combo.find(i => i.supplierId)?.supplierId,
      accountId: combo.find(i => i.accountId)?.accountId,
      unitOfMeasureId: unitItem?.unitOfMeasureId,
      unitCode: unitItem?.unitCode,
    };
    // Outer-axis group labels — unit-suffixed like the leaf label, so a product-group axis in an
    // OUTER position still shows "Markberedning (ha)" on its grouping rows (spec §12.3).
    for (let k = 0; k < lastIdx; k++) row[`g${k}`] = rowItemLabel(combo[k]);
    for (const p of periods) {
      const cell = saved.get(`${id}|${p.id}`);
      if (!cell) continue;
      const mv = cell.measureValues;
      for (const m of measures) {
        if (mv[m.code] === undefined) continue;
        if (isInternalRevenueMeasure(m)) {
          // A rate that does not vary by period (spec §6.2): shown once per row, not per period.
          row[`${INTERNAL_PRICE_FIELD_PREFIX}${m.code}`] = mv[m.code];
          continue;
        }
        row[`${p.id}__${m.code}`] = mv[m.code];
      }
      // A PENDING cell (e.g. no internal price matched) flags the row's Internpris cell — never a
      // silently blank derived value. First reason wins; every period of a row pends alike in v1.
      if (internalRevenueMeasures.length > 0
          && cell.mappingStatus === 'PENDING'
          && row[INTERNAL_PRICE_PENDING_FIELD] === undefined) {
        row[INTERNAL_PRICE_PENDING_FIELD] = cell.mappingReason ?? 'PENDING';
      }
    }
    built.push(row);
  }
  return built;
}

/**
 * One per-unit bucket of a cross-row quantity aggregate (internal-pricelist spec §12.3): the unit
 * and the sum of the contributing rows that carry it. Unit-less rows bucket under null. Buckets are
 * NEVER summed with each other — "5 ha + 40 tim" is not a number.
 */
export interface UnitTotal { unitId: string | null; unitCode: string | null; sum: number }

/**
 * Sum cross-row aggregate entries per unit (first-appearance unit order). Blank/NaN values are
 * skipped; each bucket's sum is float-noise-cleaned. The Show Totals aggregation rows feed each
 * contributing row's cell value + unit through here — one bucket per distinct unit, a null bucket
 * for rows with no resolvable unit.
 */
export function sumByUnit(
  entries: Array<{ value: unknown; unitId?: string; unitCode?: string }>,
): UnitTotal[] {
  const buckets = new Map<string | null, UnitTotal>();
  for (const e of entries) {
    if (e.value === undefined || e.value === null || e.value === '') continue;
    const n = Number(e.value);
    if (Number.isNaN(n)) continue;
    const key = e.unitId ?? null;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { unitId: key, unitCode: e.unitCode ?? null, sum: 0 };
      buckets.set(key, bucket);
    }
    bucket.sum += n;
  }
  const out = [...buckets.values()];
  for (const b of out) b.sum = cleanFloat(b.sum);
  return out;
}

/**
 * Render per-unit totals (spec §12.3): one bucket → a plain formatted number (no suffix — a
 * uniform scope reads as today); several → a compact per-unit breakdown "1 250 ha · 8 400 tim"
 * (NBSP before the code so a bucket never wraps mid-token; " · " between buckets). A null-unit
 * bucket renders bare and last. Never a sum across buckets.
 */
export function formatUnitTotals(
  totals: UnitTotal[],
  format: (value: number) => string,
): string {
  if (totals.length === 0) return '';
  if (totals.length === 1) {
    return format(totals[0].sum);
  }
  const ordered = [...totals].sort((a, b) => Number(a.unitId === null) - Number(b.unitId === null));
  return ordered
    .map(t => (t.unitCode ? `${format(t.sum)} ${t.unitCode}` : format(t.sum)))
    .join(' · ');
}

/**
 * One month's share in a DistributionProfile — a percent of a normal month (100 = normal, 0 = skip).
 * Mirrors the DistributionProfileLine entity (month + sharePercent).
 */
export interface ProfileShare { month: string; sharePercent: number }

/** FiscalMonth enum codes in fiscal-year order; the index + 1 is the period number (Jan = period 1). */
const FISCAL_MONTHS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
];

/** FiscalMonth code → period number (1..12); 0 if the code is unknown. */
export function monthToPeriodNumber(code: string): number {
  return FISCAL_MONTHS.indexOf(code) + 1;
}

/** An additive INPUT measure (QUANTITY/AMOUNT) is spread top-down: the total is divided across months. */
export function isSpreadableMeasure(measure: MeasureLike): boolean {
  return measure.kind !== 'DERIVED' && isAdditiveMeasure(measure.valueType);
}

/**
 * A non-additive INPUT measure (RATE/RATIO, e.g. Pris) is *broadcast* top-down, not spread: its total
 * is not a sum, so a typed value is copied to every month rather than divided.
 */
export function isBroadcastMeasure(measure: MeasureLike): boolean {
  return measure.kind !== 'DERIVED' && !isAdditiveMeasure(measure.valueType);
}

/** Any INPUT measure supports top-down entry (additive → spread, rate/ratio → broadcast). */
export function isTopDownMeasure(measure: MeasureLike): boolean {
  return isSpreadableMeasure(measure) || isBroadcastMeasure(measure);
}

/** Decimal granularity a spread rounds to — the measure's display decimals (QUANTITY → 0, AMOUNT → 2). */
function spreadDecimals(measure: Pick<MeasureLike, 'valueType' | 'displayDecimals'>): number {
  return measureFormatOptions(measure).maximumFractionDigits ?? 0;
}

/**
 * The spread weight (share) of each grid period, from a profile's per-month shares matched by the period's
 * month number. **Every month is normal (100) unless a profile line overrides it** — so only the months
 * that differ need a line, and an empty/undefined profile is an even split (all 100). Shares are relative;
 * `spreadTotal` normalizes them, so the absolute 100 baseline never has to sum to anything.
 */
export function resolveWeights(
  profileShares: ProfileShare[] | undefined,
  periods: PeriodLike[],
): number[] {
  const byMonth = new Map((profileShares ?? []).map(s => [monthToPeriodNumber(s.month), s.sharePercent]));
  return periods.map(p => byMonth.get(p.periodNumber ?? 0) ?? 100);
}

/**
 * Spread a typed total across `periods` by relative `weights` (normalized: month_i = total × w_i / Σw),
 * rounded to `decimals` places with the **largest-remainder** method so Σ months === total exactly (at
 * that precision — no öre/unit drift). A 0 weight gets nothing; an all-zero/empty weight set falls back to
 * an even split. Sign is preserved (the magnitude is spread, then re-signed). Returns a map periodId → value.
 */
export function spreadTotal(
  total: number,
  periods: PeriodLike[],
  weights: number[],
  decimals: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (periods.length === 0) return out;

  let w = weights.slice(0, periods.length);
  while (w.length < periods.length) w.push(0);
  let sum = w.reduce((a, b) => a + (b > 0 ? b : 0), 0);
  if (sum <= 0) { w = periods.map(() => 1); sum = periods.length; } // all-zero ⇒ even fallback

  const sign = total < 0 ? -1 : 1;
  const scale = Math.pow(10, decimals);
  const totalUnits = Math.round(Math.abs(total) * scale);

  const floors = w.map(wi => Math.floor((totalUnits * Math.max(wi, 0)) / sum));
  const fracs = w.map((wi, i) => ((totalUnits * Math.max(wi, 0)) / sum) - floors[i]);
  let remainder = totalUnits - floors.reduce((a, b) => a + b, 0);

  // Hand the residual one unit at a time to the largest fractional remainders (positive-weight only).
  const order = w
    .map((wi, i) => ({ i, wi, frac: fracs[i] }))
    .filter(e => e.wi > 0)
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; k < order.length && remainder > 0; k++, remainder--) floors[order[k].i] += 1;

  periods.forEach((p, i) => { out[p.id] = (sign * floors[i]) / scale; });
  return out;
}

/**
 * Apply a top-down spread of `total` for one additive INPUT `measure` onto a row in place: writes each
 * month cell `${periodId}__${code}`, then recomputes the row's Total cells. Returns the same row. The
 * caller is responsible for re-evaluating DERIVED measures and persisting via composeSaveCells.
 */
export function spreadTotalIntoRow(
  row: EntryRow,
  measure: MeasureLike,
  total: number,
  periods: PeriodLike[],
  weights: number[],
  allMeasures: MeasureLike[],
): EntryRow {
  const split = spreadTotal(total, periods, weights, spreadDecimals(measure));
  for (const p of periods) row[`${p.id}__${measure.code}`] = split[p.id];
  return applyRowTotals(row, allMeasures, periods);
}

/**
 * Apply a top-down *broadcast* for one rate/ratio INPUT `measure` onto a row in place: copy `value` to
 * every month cell `${periodId}__${code}` (a price is the same each month, not divided), then recompute
 * the row's Total cells. Returns the same row.
 */
export function broadcastIntoRow(
  row: EntryRow,
  measure: MeasureLike,
  value: number,
  periods: PeriodLike[],
  allMeasures: MeasureLike[],
): EntryRow {
  for (const p of periods) row[`${p.id}__${measure.code}`] = value;
  return applyRowTotals(row, allMeasures, periods);
}

/**
 * Compute a DERIVED measure for one period from the row's input values. v1 supports a product of measure
 * codes ("VOLUME * PRICE"); anything else (or a missing operand) yields a blank cell. The server stays the
 * source of truth on save.
 */
export function evalProduct(formula: string | undefined, row: EntryRow, periodId: string): number | '' {
  if (!formula || !formula.includes('*')) return '';
  let acc = 1;
  for (const code of formula.split('*').map(s => s.trim())) {
    const raw = row[`${periodId}__${code}`];
    const n = Number(raw);
    if (raw === undefined || raw === null || raw === '' || Number.isNaN(n)) return '';
    acc *= n;
  }
  return acc;
}

export interface RowEditConfig {
  topDownMeasures: MeasureLike[]; // INPUT measures whose Total cell is editable (spread or broadcast)
  derivedMeasures: MeasureLike[]; // DERIVED measures recomputed from inputs (e.g. Värde = Volym × Pris)
  measures: MeasureLike[];        // all measures, for total recomputation
  periods: PeriodLike[];
  weights: number[];              // spread weights for additive top-down measures
  showTotals: boolean;
}

/**
 * Apply ONE committed cell edit to a row: if the user edited an editable Total cell, spread (additive) or
 * broadcast (rate/ratio) it down to the months — or, if they BLANKED it, clear the measure across every
 * month (the inverse gesture: emptying the Total empties the row for that measure, so the save deletes
 * the stored lines); then recompute DERIVED measures and the Total cells.
 * Returns a NEW row (the input is not mutated). Pure — the grid's `processRowUpdate` is a thin wrapper.
 *
 * CRITICAL: each Total is compared between the COMMITTED row (`updated`) and the `original`, NEVER the
 * progressively-mutated working row. Spreading one measure recomputes the OTHER measures' totals (e.g.
 * spreading Volym shifts Pris's weighted-average total Σ Värde / Σ Volym); treating those recomputed
 * totals as user input would wrongly broadcast them across every month (overwriting per-month prices).
 */
export function applyRowEdit(updated: EntryRow, original: EntryRow, cfg: RowEditConfig): EntryRow {
  const isBlank = (v: unknown) => v === '' || v === undefined || v === null;
  let row: EntryRow = { ...updated };
  for (const m of cfg.topDownMeasures) {
    const field = `${TOTAL_FIELD_PREFIX}${m.code}`;
    const next = updated[field];
    const prev = original[field];
    const n = Number(next);
    if (isBlank(next) && !isBlank(prev)) {
      // The user blanked this measure's Total: clear its month cells. (`prev` must be non-blank so a
      // commit on some OTHER cell, where an untouched Total is blank on both sides — e.g. an unpaired
      // RATE — never wipes the row.)
      for (const p of cfg.periods) row[`${p.id}__${m.code}`] = '';
    } else if (next !== prev && !isBlank(next) && !Number.isNaN(n)) {
      row = isSpreadableMeasure(m)
        ? spreadTotalIntoRow(row, m, n, cfg.periods, cfg.weights, cfg.measures)
        : broadcastIntoRow(row, m, n, cfg.periods, cfg.measures);
    }
  }
  for (const p of cfg.periods) {
    for (const dm of cfg.derivedMeasures) {
      if (isInternalRevenueMeasure(dm)) continue; // server-sourced — a client recompute would blank it
      row[`${p.id}__${dm.code}`] = evalProduct(dm.formula, row, p.id);
    }
  }
  if (cfg.showTotals) applyRowTotals(row, cfg.measures, cfg.periods);
  return row;
}

/**
 * The save payload: one cell per DIRTY (row, period) — the cells the user actually touched since
 * load, keyed via {@link cellKey}. Untouched cells are never re-sent (server work is ~linear in
 * cells sent, so this is the performance lever). A dirty cell that still holds input values is an
 * upsert; a dirty cell the user blanked carries no values and is flagged `clear` so the server
 * deletes its stored line. DERIVED measures are omitted (recomputed server-side).
 */
export function composeSaveCells(
  rows: EntryRow[],
  periods: PeriodLike[],
  inputMeasures: MeasureLike[],
  dirtyCells: ReadonlySet<string>,
): SaveCell[] {
  const cells: SaveCell[] = [];
  for (const row of rows) {
    for (const p of periods) {
      if (!dirtyCells.has(cellKey(row.id, p.id))) continue;
      const measures: Record<string, number> = {};
      for (const m of inputMeasures) {
        const raw = row[`${p.id}__${m.code}`];
        if (raw === undefined || raw === null || raw === '') continue;
        const value = Number(raw);
        if (!Number.isNaN(value)) measures[m.code] = value;
      }
      const cell: SaveCell = {
        fiscalPeriodId: p.id,
        dimensionValueIds: row.dimensionValueIds,
        productGroupId: row.productGroupId,
        customerId: row.customerId,
        tradeTypeId: row.tradeTypeId,
        supplierId: row.supplierId,
        accountId: row.accountId,
        measureValues: measures,
      };
      // Dirty but no input values left → the user cleared the cell: delete the stored line. An
      // ALL-ZERO cell is a clear too: zeroing a row (typing 0, or a 0 total spread over the months)
      // is how a budgeter empties it, and a stored all-zero line carries no information — rollups and
      // reconciliation treat absence as 0, so keeping it would only clutter Plan Lines. A cell where
      // just SOME measure is 0 (e.g. Volym 0 but Pris set) is still a real value and upserts.
      const values = Object.values(measures);
      if (values.length === 0 || values.every(v => v === 0)) cell.clear = true;
      cells.push(cell);
    }
  }
  return cells;
}
